import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router';

import { EntityIcon } from 'web-components';
import { useClickAway } from 'web-hooks';
import { Flex, SVG } from 'web-styled';

import type { SearchSectionItem } from './types';
import * as S from './styled';

const searchLinkMap = {
  clients: 'users',
  exercises: 'exercises',
  workouts: 'workout_templates',
  programs: 'program_templates',
  documents: 'documents',
} as const;

type Props = {
  searchQuery: string;
  searchSections: SearchSectionItem[];
  setSearchQuery: (text: string) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onSearchSubmit: () => void;
};

const SearchSection = ({
  searchQuery,
  searchSections,
  setSearchQuery,
  onKeyDown,
  onSearchSubmit,
}: Props) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const hasQuery = Boolean(searchQuery.trim());
  const hasResults = searchSections.length > 0;
  const showDropdown = isOpen && hasQuery;
  const clickAwayRef = useClickAway(() => setIsOpen(false), { disabled: !showDropdown });
  const resultCount = useMemo(
    () => searchSections.reduce((acc, section) => acc + section.items.length, 0),
    [searchSections],
  );
  const flatResults = useMemo(
    () => searchSections.flatMap((section) => section.items),
    [searchSections],
  );

  useEffect(() => {
    setActiveIndex(-1);
  }, [searchQuery, resultCount]);

  const getResultPath = useCallback((item: SearchSectionItem['items'][number]) => (
    item.entityType === 'clients'
      ? `/${searchLinkMap[item.entityType]}/${item.id}/programs`
      : `/${searchLinkMap[item.entityType]}/${item.id}`
  ), []);

  const handleChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
  }, [setSearchQuery]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!hasQuery) {
      onKeyDown(event);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      if (!flatResults.length) return;
      setActiveIndex((currentIndex) => (currentIndex + 1) % flatResults.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      if (!flatResults.length) return;
      setActiveIndex((currentIndex) => (
        currentIndex <= 0 ? flatResults.length - 1 : currentIndex - 1
      ));
      return;
    }

    if (event.key === 'Enter') {
      if (showDropdown && activeIndex >= 0) {
        event.preventDefault();
        const activeResult = flatResults[activeIndex];

        if (!activeResult) return;

        navigate(getResultPath(activeResult));
        setSearchQuery('');
        setActiveIndex(-1);
        setIsOpen(false);
        return;
      }

      onKeyDown(event);
      setIsOpen(false);
      return;
    }

    if (event.key === 'Escape') {
      setActiveIndex(-1);
      setIsOpen(false);
      return;
    }

    onKeyDown(event);
  }, [
    activeIndex,
    flatResults,
    getResultPath,
    hasQuery,
    navigate,
    onKeyDown,
    setSearchQuery,
    showDropdown,
  ]);

  const handleClear = useCallback(() => {
    if (!searchQuery) return;
    setSearchQuery('');
    setActiveIndex(-1);
    setIsOpen(false);
  }, [searchQuery, setSearchQuery]);

  const handleFocus = useCallback(() => {
    if (hasQuery) {
      setIsOpen(true);
    }
  }, [hasQuery]);

  const handleResultClick = useCallback(() => {
    setSearchQuery('');
    setActiveIndex(-1);
    setIsOpen(false);
  }, [setSearchQuery]);

  const handleViewAllClick = useCallback(() => {
    onSearchSubmit();
    setActiveIndex(-1);
    setIsOpen(false);
  }, [onSearchSubmit]);

  return (
    <S.Search data-tour="tour__search" ref={clickAwayRef}>
      <S.InputGroup>
        <S.Input
          placeholder="Search clients, workouts, and more..."
          value={searchQuery}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        <S.SearchButton
          onClick={searchQuery ? handleClear : undefined}
          type="button"
        >
          {searchQuery ? <SVG.X size={13} /> : <SVG.Search size={13} />}
        </S.SearchButton>
      </S.InputGroup>
      {showDropdown && (
        <S.SearchDropdown>
          {hasResults ? (
            <>
              {searchSections.map((section) => (
                <S.SearchCategory key={section.key}>
                  <S.SearchCategoryTitle>{section.label}</S.SearchCategoryTitle>
                  {section.items.map((item) => (
                    <S.SearchResultLink
                      key={`${section.key}-${item.id}`}
                      to={getResultPath(item)}
                      $isActive={flatResults[activeIndex]?.entityType === item.entityType
                        && flatResults[activeIndex]?.id === item.id}
                      onClick={handleResultClick}
                      onMouseEnter={() => {
                        setActiveIndex(flatResults.findIndex((result) => (
                          result.entityType === item.entityType && result.id === item.id
                        )));
                      }}
                    >
                      <Flex spacing={10} align="center">
                        <EntityIcon resourceType={item.entityType} />
                        <S.SearchResultName>{item.name}</S.SearchResultName>
                      </Flex>
                    </S.SearchResultLink>
                  ))}
                </S.SearchCategory>
              ))}
              <S.ViewAllButton type="button" onClick={handleViewAllClick}>
                View all {resultCount} result{resultCount === 1 ? '' : 's'}
              </S.ViewAllButton>
            </>
          ) : (
            <S.SearchEmpty>No results found</S.SearchEmpty>
          )}
        </S.SearchDropdown>
      )}
    </S.Search>
  );
};

export default SearchSection;
